import { css } from 'lit';

/**
 * Shared look for the back-office tab panels (Stocks, Members, Companies,
 * Reports, Mall). Components keep their layout rules and add these on top:
 * `static styles = [TAB_UI_STYLES, css`...`]`.
 */
export const TAB_UI_STYLES = css`
  /* ── buttons ── */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem;
    min-height: 40px; padding: 0 16px;
    border: 1px solid transparent; border-radius: 12px;
    background: var(--color-primary); color: #fff;
    font-family: inherit; font-size: 0.84rem; font-weight: 600;
    cursor: pointer; white-space: nowrap;
    transition: background 0.15s, border-color 0.15s, filter 0.15s;
  }
  .btn:hover:not(:disabled) { filter: brightness(1.08); }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-sec { background: var(--color-surface); color: var(--color-text); border-color: var(--color-border); }
  .btn-sec:hover:not(:disabled) { background: #f4f7fb; border-color: #c7d2e4; filter: none; }
  .btn-danger { background: var(--color-error, #d93025); color: #fff; border-color: transparent; }
  .btn-sm { min-height: 34px; padding: 0 12px; font-size: 0.76rem; border-radius: 10px; }
  /* Standalone small buttons (markup uses .btn-sm without .btn) */
  .btn-sm:not(.btn):not(.btn-sec):not(.btn-danger) {
    display: inline-flex; align-items: center; justify-content: center; gap: 0.3rem;
    border: 1px solid var(--color-border); background: var(--color-surface); color: var(--color-text);
    font-family: inherit; font-weight: 600; cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .btn-sm:not(.btn):not(.btn-sec):not(.btn-danger):hover:not(:disabled) { background: #f4f7fb; border-color: #c7d2e4; }
  .btn-cancel {
    display: inline-flex; align-items: center; justify-content: center;
    min-height: 40px; padding: 0 16px;
    border: 1px solid var(--color-border); border-radius: 12px;
    background: var(--color-surface); color: var(--color-text);
    font-family: inherit; font-size: 0.84rem; font-weight: 600; cursor: pointer;
  }
  .btn-cancel:hover:not(:disabled) { background: #f4f7fb; }
  .btn-submit {
    display: inline-flex; align-items: center; justify-content: center;
    min-height: 40px; padding: 0 16px;
    border: none; border-radius: 12px;
    background: var(--color-primary); color: #fff;
    font-family: inherit; font-size: 0.84rem; font-weight: 600; cursor: pointer;
  }
  .btn-submit:hover:not(:disabled) { filter: brightness(1.08); }
  .btn-submit:disabled, .btn-cancel:disabled { opacity: 0.55; cursor: not-allowed; }

  /* ── tabs strip ── */
  .tabs { display: flex; gap: 0.15rem; border-bottom: 1px solid var(--color-border); margin-bottom: 0.9rem; overflow-x: auto; flex-shrink: 0; scrollbar-width: none; }
  .tabs::-webkit-scrollbar { display: none; }
  .tabs button {
    flex-shrink: 0; min-height: 42px; padding: 0 14px;
    background: none; border: none; border-bottom: 2px solid transparent;
    font-family: inherit; font-size: 0.83rem; font-weight: 600;
    color: var(--color-text-secondary); cursor: pointer;
  }
  .tabs button:hover { color: var(--color-primary); }
  .tabs button.active { color: var(--color-primary); border-bottom-color: var(--color-primary); }

  /* ── tables ── */
  table { width: 100%; border-collapse: collapse; font-size: 0.83rem; }
  th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--color-border); }
  th { font-size: 0.72rem; font-weight: 700; color: var(--color-text-secondary); text-transform: uppercase; letter-spacing: 0.03em; }
  th.num, td.num { text-align: right; }
  tbody tr:hover { background: #f8fafd; }
  tr.clickable { cursor: pointer; }
  td input, td select {
    width: 100%; min-height: 34px; padding: 4px 8px; box-sizing: border-box;
    border: 1px solid var(--color-border); border-radius: 8px;
    background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.8rem;
  }
  td input:focus, td select:focus { outline: none; border-color: var(--color-primary); }

  /* ── form fields ── */
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 0.8rem 1rem; }
  .fg label, .form-group label {
    display: block; font-size: 0.72rem; font-weight: 700;
    color: var(--color-text-secondary); margin-bottom: 0.3rem;
  }
  .fg input, .fg select, .fg textarea, .form-group input, .form-group select, .form-group textarea {
    width: 100%; min-height: 40px; padding: 9px 12px; box-sizing: border-box;
    border: 1px solid var(--color-border); border-radius: 10px;
    background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.86rem;
  }
  /* Radios/checkboxes are inline controls, not text fields. Without this they
     inherit the 100%-wide, 40px-tall input sizing and look like giant boxes. */
  .fg input[type="checkbox"], .fg input[type="radio"],
  .form-group input[type="checkbox"], .form-group input[type="radio"] {
    width: auto; min-height: 0; height: 1.15rem; padding: 0; margin: 0;
    border: 0; border-radius: 0; background: transparent; box-shadow: none;
    flex: 0 0 auto; accent-color: var(--color-primary);
  }
  .fg input:focus, .fg select:focus, .fg textarea:focus,
  .form-group input:focus, .form-group select:focus, .form-group textarea:focus {
    outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.16);
  }
  .fg input:disabled, .fg select:disabled, .fg textarea:disabled { background: #f8fafc; color: var(--color-text-secondary); }
  .fg textarea, .form-group textarea { min-height: 60px; resize: vertical; }
  .list-tools input, .search-row input, .search-bar input, .filter-row input {
    min-height: 40px; padding: 9px 12px; box-sizing: border-box;
    border: 1px solid var(--color-border); border-radius: 10px;
    background: var(--color-surface); color: var(--color-text); font-family: inherit; font-size: 0.86rem;
  }
  .list-tools input:focus, .search-row input:focus, .search-bar input:focus, .filter-row input:focus {
    outline: none; border-color: var(--color-primary); box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.16);
  }
  .ro { padding: 9px 2px; font-size: 0.85rem; color: var(--color-text); word-break: break-word; white-space: pre-wrap; }
  .fg.full { grid-column: 1 / -1; }

  /* ── panels ── */
  .section { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 14px; padding: 1rem; margin-bottom: 1rem; }
  .section h4 { margin: 0 0 0.7rem; font-size: 0.88rem; }
  .detail-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem; margin-bottom: 0.75rem; flex-shrink: 0; }
  .detail-head h3 { margin: 0; font-size: 1.05rem; }
  .badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 999px; background: #e8f0fe; color: var(--color-primary); font-size: 0.72rem; font-weight: 700; }
  .spacer { flex: 1; }
  .chip {
    display: inline-flex; align-items: center; min-height: 38px; padding: 0 14px;
    border-radius: 999px; border: 1px solid var(--color-border);
    background: var(--color-surface); color: var(--color-text-secondary);
    font-family: inherit; font-size: 0.84rem; font-weight: 600; cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .chip:hover { border-color: var(--color-primary); color: var(--color-primary); }
  .chip.selected { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }

  /* ── states ── */
  .loading, .empty { text-align: center; padding: 1.4rem; color: var(--color-text-secondary); font-size: 0.85rem; }
  .err, .error { color: var(--color-error); font-size: 0.8rem; margin: 0.5rem 0; }
  .ok { color: var(--color-success); font-size: 0.8rem; margin: 0.5rem 0; }
  .hint, .muted { color: var(--color-text-secondary); font-size: 0.78rem; }
  .hint { margin: 0 0 0.55rem; }

  /* ── modal (padded card that scrolls as a whole) ── */
  .modal-overlay {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(15, 23, 42, 0.5);
    display: flex; align-items: center; justify-content: center; padding: 1rem;
  }
  .modal {
    background: var(--color-surface); color: var(--color-text);
    border-radius: 16px; width: 100%; max-width: 560px;
    max-height: 86dvh; overflow-y: auto; box-sizing: border-box;
    padding: 1.15rem 1.2rem;
    box-shadow: 0 24px 60px rgba(15, 35, 80, 0.3);
  }
  .modal h3, .modal h4 { margin: 0 0 0.75rem; }
  .modal .btns, .modal-footer { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem; flex-wrap: wrap; }

  @media (max-width: 768px) {
    .btn, .btn-sec, .btn-danger, .btn-cancel, .btn-submit { min-height: 44px; }
    .btn-sm, .btn-sm:not(.btn):not(.btn-sec):not(.btn-danger) { min-height: 40px; }
    .chip { min-height: 42px; }
    .fg input, .fg select, .form-group input, .form-group select,
    .list-tools input, .search-row input, .search-bar input, .filter-row input { min-height: 48px; font-size: 16px; }
    .fg textarea, .form-group textarea { font-size: 16px; }
    td input, td select { min-height: 40px; font-size: 16px; }
    .modal-overlay { align-items: flex-end; padding: 0; }
    .modal {
      max-width: none; border-radius: 18px 18px 0 0; max-height: 92dvh;
      padding-bottom: calc(1.2rem + env(safe-area-inset-bottom));
    }
  }

  @media (prefers-reduced-motion: reduce) {
    * { animation-duration: 0.01ms !important; transition: none !important; }
  }
`;
